import React, { useEffect, useState } from 'react';
import { 
    DocumentCard, 
    DocumentCardDetails, 
    DocumentCardType, 
    Icon, 
    IDocumentCardStyles, 
    ITextStyles, 
    Text, 
} from '@fluentui/react';
import LoaderComponent from '../../components/loader/loader.component';
import { MapContainer, TileLayer } from 'react-leaflet';
import { LatLng } from 'leaflet';
import LeafletMapMarkerComponent from '../../components/map-marker/leaflet-map-marker.component';
import { AzureDataPosition, AzureMap, AzureMapDataSourceProvider, AzureMapsProvider } from 'react-azure-maps';
import { AuthenticationType } from 'azure-maps-control';
import AzureMapMarkerComponent from '../../components/map-marker/azure-map-marker.component';
import GoogleMapsMapComponent from '../../components/google-maps/google-maps-map.component';
import GoogleMapMarkerComponent from '../../components/map-marker/google-map-marker.component';
import ErrorMessageComponent from '../../components/error-message/error-message.component';
import queryString from "query-string";
import { Buffer } from "buffer";

import ApiService from '../../services/api/api.service';

import ItemRegistroTiempo from '../../models/ItemRegistroTiempo';
import ItemUsuario from '../../models/ItemUsuario';

import 'leaflet/dist/leaflet.css';

const getLeafletLatLng = (value: string) : LatLng => {
    if (value.includes(',')) {
        const valueArray : string[] = value.split(',');
        return new LatLng(parseFloat(valueArray[0]), parseFloat(valueArray[1]));
    }
    else {
        const valueArray : string[] = value.split(';');
        return new LatLng(parseFloat(valueArray[0]), parseFloat(valueArray[1]));
    }
};

const getAzureLatLng = (value: string) : AzureDataPosition => {
    if (value.includes(',')) {
        const valueArray : string[] = value.split(',');
        return [parseFloat(valueArray[1]), parseFloat(valueArray[0])];
    }
    else {
        const valueArray : string[] = value.split(';');
        return [parseFloat(valueArray[1]), parseFloat(valueArray[0])];
    }
};

const getGoogleLatLng = (value: string) : google.maps.LatLngLiteral => {
    if (value.includes(',')) {
        const valueArray : string[] = value.split(',');
        return {
            lat: parseFloat(valueArray[0]),
            lng: parseFloat(valueArray[1]),
        };
    }
    else {
        const valueArray : string[] = value.split(';');
        return {
            lat: parseFloat(valueArray[0]),
            lng: parseFloat(valueArray[1]),
        };
    }
};

const cardStyle: IDocumentCardStyles = {
    root: {
        backgroundColor: "#ffffff",
        minWidth: "calc(100% - 20px)",
        padding: 10,
        color: "rgb(50, 49, 48)",
        height: 'auto',
    },
};

const selectedCardStyle: IDocumentCardStyles = {
    root: {
        backgroundColor: "#ed6d63",
        minWidth: "calc(100% - 20px)",
        padding: 10,
        borderColor: "#ed6d63",
        color: "#ffffff",
        height: 'auto',
        selectors: {
            ":hover": {
                borderColor: "#ed6d63",
            },
        },
    },
};

const textStyles: ITextStyles = {
    root: {
        color: 'inherit',
    },
};

interface IChronologyReportPageProps {};

interface IChronologyReportPageParams {
    IdUsuario: string;
    CodigoCliente: string;
    TokenSesion: string;
    IdOrden: number;
    IdTarea: number;
    FechaDesde: Date;
    FechaHasta: Date;
    Usuario: string;
};

const ChronologyReportPage = (props: IChronologyReportPageProps) => {
    const mapType : string = "google";

    const [params, setParams] = useState<IChronologyReportPageParams>();
    const [user, setUser] = useState<ItemUsuario>();
    const [loading, setLoading] = useState<boolean>(true);
    const [workParts, setWorkParts] = useState<ItemRegistroTiempo[]>([]);
    const [selectedWorkPart, setSelectedWorkPart] = useState<ItemRegistroTiempo>();
    const [selectedStart, setSelectedStart] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    useEffect(() => {
        let queryParams : queryString.ParsedQuery<string> = queryString.parse(window.location.search);
        setParams(JSON.parse(Buffer.from(queryParams.ItemCronologia?.toString() || "", 'base64').toString('ascii')));
    }, []);

    useEffect(() => {
        if (params) {
            let user : ItemUsuario = new ItemUsuario();
            user.IdUsuario = params.IdUsuario;
            user.CodigoCliente = params.CodigoCliente;
            user.Token = params.TokenSesion;
            setUser(user);
        }
    }, [params]);

    useEffect(() => {
        if (user && params) {
            setLoading(true);
            setError('');
            ApiService.Post<ItemRegistroTiempo[]>('OrdenesTrabajo', 'GetTareaTiempos', user, {
                "IdOrden": params.IdOrden,
                "IdTarea": params.IdTarea,
                "FechaDesde": params.FechaDesde,
                "FechaHasta": params.FechaHasta,
                "Usuario": params.Usuario,
            }, false).then((response: ItemRegistroTiempo[]) => {
                setWorkParts(response);
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }
    }, [user, params]);

    useEffect(() => {
        if (workParts.length) {
            setSelectedWorkPart(workParts[0]);
            setSelectedStart(true);
            setError('');
        }
        else {
            setSelectedWorkPart(undefined);
            setSelectedStart(false);
            setError('No hay datos para mostrar');
        }
    }, [workParts]);

    const onRowClick = (item: ItemRegistroTiempo, start: boolean) : void => {
        setSelectedWorkPart(item);
        setSelectedStart(start);
    };

    const onSelectMapMarker = (item: ItemRegistroTiempo, start: boolean) : void => {
        setSelectedWorkPart(item);
        setSelectedStart(start);
    };

    return (
        <div className='d-flex flex-column h-100 w-100'>
            {
                (!loading && !error) ? (
                    <div className="row m-0 flex-fill overflow-hidden">
                        <div className="col-12 col-md-6 h-50 h-md-100 px-2 overflow-auto">
                            <div className="row m-0">
                                <div className="col-12 py-3">
                                    {
                                        workParts.map((workPart: ItemRegistroTiempo) => 
                                            <div key={workPart.Id}>
                                                {
                                                    workPart.PosicionGPSInicio ? (
                                                        <DocumentCard 
                                                            styles={(workPart.Id === selectedWorkPart?.Id && selectedStart) ? selectedCardStyle : cardStyle}
                                                            type={DocumentCardType.compact}
                                                            onClick={() => onRowClick(workPart, true)}>
                                                            <DocumentCardDetails>
                                                                <Icon iconName='Play' />
                                                                <Text 
                                                                    variant="medium" 
                                                                    styles={textStyles}>
                                                                    <strong>
                                                                        {workPart.IdUsuario}
                                                                    </strong> - {workPart.CodigoCliente}
                                                                </Text>
                                                                <Text styles={textStyles}>
                                                                    OT: {workPart.IdOrden}
                                                                </Text>
                                                                <Text 
                                                                    variant="xSmall"
                                                                    styles={textStyles}>
                                                                    {workPart.Inicio}
                                                                </Text>
                                                                <Text styles={textStyles}>
                                                                    {workPart.Descripcion}
                                                                </Text>
                                                            </DocumentCardDetails>
                                                        </DocumentCard>
                                                    ) : (
                                                        null
                                                    )
                                                }
                                                {
                                                    workPart.PosicionGPSFinal ? (
                                                        <DocumentCard 
                                                            styles={(workPart.Id === selectedWorkPart?.Id && !selectedStart) ? selectedCardStyle : cardStyle}
                                                            type={DocumentCardType.compact}
                                                            onClick={() => onRowClick(workPart, false)}>
                                                            <DocumentCardDetails>
                                                                <Icon iconName='Pause' />
                                                                <Text 
                                                                    variant="medium"
                                                                    styles={textStyles}>
                                                                    <strong>
                                                                        {workPart.IdUsuario}
                                                                    </strong> - {workPart.CodigoCliente}
                                                                </Text>
                                                                <Text styles={textStyles}>
                                                                    OT: {workPart.IdOrden}
                                                                </Text>
                                                                <Text 
                                                                    variant="xSmall"
                                                                    styles={textStyles}>
                                                                    {workPart.Fin}
                                                                </Text>
                                                                <Text styles={textStyles}>
                                                                    {workPart.Descripcion}
                                                                </Text>
                                                            </DocumentCardDetails>
                                                        </DocumentCard>
                                                    ) : (
                                                        null
                                                    )
                                                }
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-6 h-50 h-md-100 overflow-hidden px-2">
                            {
                                workParts.length ? (
                                    (mapType === "leaflet") ? (
                                        <MapContainer 
                                            center={getLeafletLatLng(workParts[0].PosicionGPSInicio)} 
                                            zoom={13}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                minHeight: 400,
                                            }}>
                                            <TileLayer
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                            {
                                                workParts.map((wp: ItemRegistroTiempo) => (
                                                    <LeafletMapMarkerComponent
                                                        key={wp.Id}
                                                        workPart={wp}
                                                        selectedWorkPart={selectedWorkPart}
                                                        selectedStart={selectedStart}
                                                        onSelect={onSelectMapMarker} />
                                                ))
                                            }
                                        </MapContainer>
                                    ) : (mapType === "azure") ? (
                                        <AzureMapsProvider>
                                            <div style={{ 
                                                width: '100%',
                                                height: '100%',
                                                minHeight: 400,
                                            }}>
                                                <AzureMap 
                                                    options={{
                                                        authOptions: {
                                                            authType: AuthenticationType.subscriptionKey,
                                                            subscriptionKey: '3Qqlzj_T3oymm37vAdIMIs2H4jUbPZXxtbkHg03LONw',
                                                        },
                                                        center: getAzureLatLng(workParts[0].PosicionGPSInicio),
                                                        zoom: 13,
                                                    }}>
                                                    <AzureMapDataSourceProvider 
                                                        id={'AzureMapDataSourceProvider'}
                                                        options={{
                                                            lineMetrics: true,
                                                        }}>
                                                        {
                                                            workParts.map((wp: ItemRegistroTiempo) => (
                                                                <AzureMapMarkerComponent
                                                                    key={wp.Id}
                                                                    workPart={wp}
                                                                    selectedWorkPart={selectedWorkPart}
                                                                    selectedStart={selectedStart}
                                                                    onSelect={onSelectMapMarker} />
                                                            ))
                                                        }
                                                    </AzureMapDataSourceProvider>
                                                </AzureMap>
                                            </div>
                                        </AzureMapsProvider>
                                    ) : (mapType === "google") ? (
                                        <GoogleMapsMapComponent
                                            zoom={13}
                                            center={getGoogleLatLng(workParts[0].PosicionGPSInicio)}>
                                            {
                                                workParts.map((wp: ItemRegistroTiempo) => (
                                                    <GoogleMapMarkerComponent
                                                        key={wp.Id}
                                                        workPart={wp}
                                                        selectedWorkPart={selectedWorkPart}
                                                        selectedStart={selectedStart}
                                                        onSelect={onSelectMapMarker} />
                                                ))
                                            }
                                        </GoogleMapsMapComponent>
                                    ) : (
                                        null
                                    )
                                ) : (
                                    null
                                )
                            }
                        </div>
                    </div>
                ) : (
                    null
                )
            }
            {
                !loading ? (
                    <ErrorMessageComponent message={error} />
                ) : (
                    null
                )
            }
            <LoaderComponent loading={loading} />
        </div>
    );
};

export default ChronologyReportPage;
